import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box, CircularProgress } from '@mui/material';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getInviteLandlordAction } from 'store/actions/userActions';
import { InviteLandlordTable } from 'components/Tables/InviteLandlordTable';
import { useNavigate } from 'react-router-dom';
import { Button, DatePickerInput, Dropdown, Loading } from '@leapeasy/ui-kit';
import { Layerbar } from 'components/common/Layerbar';
import { EmailEditor } from 'components/EmailSender';
import { failedNotif, successNotif } from 'store/actions/notifActions';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { getInviteLandlord } from 'store/sagas/APIs/user';
import { fetchAllPaginated } from 'utils/exportPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildOrgLandlordFilterChips,
  parseOrgLandlordFilterChipId,
} from 'utils/listTableActiveFilterChips';

const parseLandlordInviteFilters = (searchParams, _paramKey, fallback) => ({
  selectedBuilings:
    searchParams.get('propertyCount') || searchParams.get('buildings')
      ? (searchParams.get('propertyCount') || searchParams.get('buildings'))
          .split(',')
          .filter(Boolean)
      : fallback.selectedBuilings,
  selectedUnits: searchParams.get('units')
    ? searchParams.get('units').split(',').filter(Boolean)
    : fallback.selectedUnits,
  selectedInvitedDate: searchParams.get('invitedDate') || fallback.selectedInvitedDate || '',
});

const serializeLandlordInviteFilters = (filters) => ({
  propertyCount:
    Array.isArray(filters.selectedBuilings) && filters.selectedBuilings.length > 0
      ? filters.selectedBuilings.join(',')
      : '',
  units:
    Array.isArray(filters.selectedUnits) && filters.selectedUnits.length > 0
      ? filters.selectedUnits.join(',')
      : '',
  invitedDate: filters.selectedInvitedDate || '',
});

const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/** Fallback if no success/error toast arrives (BE refresh can take ~20s). */
const SALESFORCE_REFRESH_FALLBACK_MS = 90000;

export const CompanyInvite = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: ['sf_landlord_id', 'email_address', 'name'],
      searchText: '',
      searchPlaceholder: 'Search Operator..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: {
      selectedBuilings: [],
      selectedUnits: [],
      selectedInvitedDate: '',
    },
    parseExtraFilters: parseLandlordInviteFilters,
    serializeExtraFilters: serializeLandlordInviteFilters,
  });

  // Dropdown options
  const [selectedBuilings, setSelectedBuildings] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [selectedInvitedDate, setSelectedInvitedDate] = useState('');
  const [selectedBuilingsDraft, setSelectedBuildingsDraft] = useState([]);
  const [selectedUnitsDraft, setSelectedUnitsDraft] = useState([]);
  const [selectedInvitedDateDraft, setSelectedInvitedDateDraft] = useState('');
  const salesforceRefreshTimeoutRef = useRef(null);
  const [salesforceRefreshLoading, setSalesforceRefreshLoading] = useState(false);
  const [awaitingSalesforceRefresh, setAwaitingSalesforceRefresh] = useState(false);

  const landlords = useSelector((state) => state.getIn(['user', 'invite_landlord']));
  const landlordsJSON = landlords ? landlords.toJS() : null;
  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(landlordsJSON?.total) || 0,
    isLoading: Boolean(landlordsJSON?.isLoading),
  });

  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedBuildings = Array.isArray(extraFilters.selectedBuilings)
      ? extraFilters.selectedBuilings
      : [];
    const nextSelectedUnits = Array.isArray(extraFilters.selectedUnits)
      ? extraFilters.selectedUnits
      : [];
    const nextSelectedInvitedDate = extraFilters.selectedInvitedDate || '';
    setSelectedBuildings((prev) =>
      areArraysEqual(prev, nextSelectedBuildings) ? prev : nextSelectedBuildings,
    );
    setSelectedUnits((prev) =>
      areArraysEqual(prev, nextSelectedUnits) ? prev : nextSelectedUnits,
    );
    setSelectedInvitedDate((prev) =>
      prev === nextSelectedInvitedDate ? prev : nextSelectedInvitedDate,
    );
    setSelectedBuildingsDraft((prev) =>
      areArraysEqual(prev, nextSelectedBuildings) ? prev : nextSelectedBuildings,
    );
    setSelectedUnitsDraft((prev) =>
      areArraysEqual(prev, nextSelectedUnits) ? prev : nextSelectedUnits,
    );
    setSelectedInvitedDateDraft((prev) =>
      prev === nextSelectedInvitedDate ? prev : nextSelectedInvitedDate,
    );
  }, [extraFilters]);

  const buildLandlordListQuery = useCallback(
    () => ({
      filter: {
        ...filter,
        selectedBuilings,
        selectedUnits,
        selectedInvitedDate,
      },
      offset: pagination.pageNumber * pagination.rowsPerPage,
      limit: pagination.rowsPerPage,
      sortOptions,
    }),
    [
      filter,
      selectedBuilings,
      selectedUnits,
      selectedInvitedDate,
      pagination.pageNumber,
      pagination.rowsPerPage,
      sortOptions,
    ],
  );

  useEffect(() => {
    dispatch(
      getInviteLandlordAction({
        type: 'fetch',
        ...buildLandlordListQuery(),
      }),
    );
  }, [dispatch, buildLandlordListQuery]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getInviteLandlord({
          type: 'fetch',
          filter: {
            options: ['name', 'email_address'],
            searchText: '',
            searchPlaceholder: 'Search Operator...',
            selectedBuilings: [],
            selectedUnits: [],
            selectedInvitedDate: '',
          },
          offset: 0,
          limit: 1,
          sortOptions,
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortOptions]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(landlordsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getInviteLandlord({
            type: 'fetch',
            filter: {
              ...filter,
              selectedBuilings,
              selectedUnits,
              selectedInvitedDate,
            },
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (e) {
      throw new Error('Unable to export operator invites right now.');
    }
  }, [
    landlordsJSON?.total,
    filter,
    selectedBuilings,
    selectedUnits,
    selectedInvitedDate,
    sortOptions,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords = Number(fullExportDatasetTotal) || Number(landlordsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getInviteLandlord({
            type: 'fetch',
            filter: {
              options: ['name', 'email_address'],
              searchText: '',
              searchPlaceholder: 'Search Operator...',
              selectedBuilings: [],
              selectedUnits: [],
              selectedInvitedDate: '',
            },
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (e) {
      throw new Error('Unable to export full operator invites dataset right now.');
    }
  }, [fullExportDatasetTotal, landlordsJSON?.total, sortOptions]);

  const filterFunc = () => {
    if (!landlords) return;
    // List numbers of buildings
    let tmp_num_buildings = [];
    let tmp_num_units = [];
    for (let i = 0; i < landlordsJSON.data.length; i++) {
      tmp_num_buildings.filter(
        (building, _) => building.value === landlordsJSON.data[i].total_buildings,
      ).length === 0 &&
        tmp_num_buildings.push({
          text: landlordsJSON.data[i].total_buildings,
          value: landlordsJSON.data[i].total_buildings,
        });

      tmp_num_units.filter((building, _) => building.value === landlordsJSON.data[i].total_units)
        .length === 0 &&
        tmp_num_units.push({
          text: landlordsJSON.data[i].total_units,
          value: landlordsJSON.data[i].total_units,
        });
    }
    tmp_num_buildings.sort(function (a, b) {
      return a.value - b.value;
    });
    tmp_num_units.sort(function (a, b) {
      return a.value - b.value;
    });
    setNumOfBuildings([...tmp_num_buildings]);
    setNumOfUnits([...tmp_num_units]);
  };

  useEffect(() => {
    filterFunc();
  }, [landlords]);

  const onClickLandlord = useCallback(
    (ll) => {
      const landlordId = ll?.sf_landlord_id;
      if (!landlordId) {
        dispatch(failedNotif('Operator ID is missing.'));
        return;
      }
      navigate(`/operator-invite/${landlordId}/detail/report`);
    },
    [dispatch, navigate],
  );

  const [numOfBuildings, setNumOfBuildings] = useState([]);
  const [numOfUnits, setNumOfUnits] = useState([]);

  const applyFunc = useCallback(() => {
    setSelectedBuildings(selectedBuilingsDraft);
    setSelectedUnits(selectedUnitsDraft);
    setSelectedInvitedDate(selectedInvitedDateDraft);
    setExtraFilters({
      selectedBuilings: selectedBuilingsDraft,
      selectedUnits: selectedUnitsDraft,
      selectedInvitedDate: selectedInvitedDateDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedBuilingsDraft,
    selectedUnitsDraft,
    selectedInvitedDateDraft,
    setExtraFilters,
    setPagination,
  ]);

  const resetFunc = useCallback(() => {
    setSelectedBuildings([]);
    setSelectedUnits([]);
    setSelectedInvitedDate('');
    setSelectedBuildingsDraft([]);
    setSelectedUnitsDraft([]);
    setSelectedInvitedDateDraft('');
    setExtraFilters({
      selectedBuilings: [],
      selectedUnits: [],
      selectedInvitedDate: '',
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const listFilterChips = useMemo(
    () =>
      buildOrgLandlordFilterChips({
        selectedBuilings,
        selectedUnits,
        selectedInvitedDate,
      }),
    [selectedBuilings, selectedUnits, selectedInvitedDate],
  );

  const handleRemoveOrgLandlordFilterChip = useCallback(
    (chipId) => {
      const parsed = parseOrgLandlordFilterChipId(chipId);
      if (!parsed) return;
      const bumpPage = () =>
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      if (parsed.type === 'invitedDate') {
        setSelectedInvitedDate('');
        setSelectedInvitedDateDraft('');
        setExtraFilters((prev) => ({ ...prev, selectedInvitedDate: '' }));
        bumpPage();
        return;
      }
      if (parsed.type === 'propertyCount') {
        const next = selectedBuilings.filter((s) => s !== parsed.value);
        setSelectedBuildings(next);
        setSelectedBuildingsDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedBuilings: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'units') {
        const next = selectedUnits.filter((s) => s !== parsed.value);
        setSelectedUnits(next);
        setSelectedUnitsDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedUnits: next }));
        bumpPage();
      }
    },
    [selectedBuilings, selectedUnits, setExtraFilters, setPagination],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveOrgLandlordFilterChip,
    resetFunc,
  );

  const [shownLandlords, setShownLandlords] = useState([]);
  const [isOpenLayber, setIsOpenLayber] = useState(false);
  const [awaitingInvite, setAwaitingInvite] = useState(false);
  const inviteLoading = useSelector((state) => state.getIn(['auth', 'loading']));
  const { hasSuccess: hasToastSuccess, hasError: hasToastError } = useNotificationMessages();

  const clearSelectedRows = useCallback(() => {
    setShownLandlords((prev) => prev.map((item) => ({ ...item, check: false })));
  }, []);

  const refetchLandlords = useCallback(() => {
    dispatch(
      getInviteLandlordAction({
        type: 'fetch',
        ...buildLandlordListQuery(),
      }),
    );
  }, [dispatch, buildLandlordListQuery]);

  const clearSalesforceRefreshLoading = useCallback(() => {
    setSalesforceRefreshLoading(false);
    setAwaitingSalesforceRefresh(false);
    if (salesforceRefreshTimeoutRef.current) {
      clearTimeout(salesforceRefreshTimeoutRef.current);
      salesforceRefreshTimeoutRef.current = null;
    }
  }, []);

  const handleRefreshFromSalesforce = useCallback(() => {
    if (salesforceRefreshLoading) return;

    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setAwaitingSalesforceRefresh(true);
    setSalesforceRefreshLoading(true);

    dispatch(
      getInviteLandlordAction({
        type: 'refresh',
        ...buildLandlordListQuery(),
      }),
    );

    if (salesforceRefreshTimeoutRef.current) {
      clearTimeout(salesforceRefreshTimeoutRef.current);
    }
    salesforceRefreshTimeoutRef.current = setTimeout(() => {
      clearSalesforceRefreshLoading();
    }, SALESFORCE_REFRESH_FALLBACK_MS);
  }, [dispatch, buildLandlordListQuery, clearSalesforceRefreshLoading, salesforceRefreshLoading]);

  useEffect(() => {
    if (!awaitingSalesforceRefresh) return;
    if (!hasToastSuccess && !hasToastError) return;
    clearSalesforceRefreshLoading();
  }, [awaitingSalesforceRefresh, hasToastSuccess, hasToastError, clearSalesforceRefreshLoading]);

  useEffect(
    () => () => {
      if (salesforceRefreshTimeoutRef.current) {
        clearTimeout(salesforceRefreshTimeoutRef.current);
      }
    },
    [],
  );

  const closeComposer = useCallback(() => {
    setAwaitingInvite(false);
    setIsOpenLayber(false);
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
  }, [dispatch]);

  const handleInviteStart = useCallback(() => {
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setAwaitingInvite(true);
  }, [dispatch]);

  useEffect(() => {
    if (!awaitingInvite || !hasToastSuccess) return;
    clearSelectedRows();
    setIsOpenLayber(false);
    setAwaitingInvite(false);
    refetchLandlords();
  }, [awaitingInvite, hasToastSuccess, clearSelectedRows, refetchLandlords]);

  useEffect(() => {
    if (awaitingInvite && hasToastError) {
      setAwaitingInvite(false);
    }
  }, [awaitingInvite, hasToastError]);

  useEffect(() => {
    if (!landlords) return;
    const nextLandlords = landlords.toJS();
    if (!nextLandlords?.data) return;
    setShownLandlords((prev) => {
      const checkedById = new Map(prev.filter((item) => item.check).map((item) => [item.id, true]));
      return nextLandlords.data.map((item) => ({
        ...item,
        check: checkedById.get(item.id) || false,
      }));
    });
  }, [landlords]);

  return (
    <DashboardLayoutContainer>
      {isOpenLayber && (
        <Layerbar>
          <EmailEditor
            setOpen={setIsOpenLayber}
            onClose={closeComposer}
            onInviteStart={handleInviteStart}
            loading={inviteLoading && awaitingInvite}
            toEmail={shownLandlords.filter((item) => item.check).map((item) => item.email_address)}
            landlordId={shownLandlords
              .filter((item) => item.check)
              .map((item) => item.sf_landlord_id)}
          />
        </Layerbar>
      )}
      {landlords ? (
        <TableArea>
          {inviteLoading && awaitingInvite && (
            <LoadingEffect>
              <Loading size="large" label="Sending invitations..." />
            </LoadingEffect>
          )}
          <SalesforceRefreshRow>
            <RefreshButtonWrap>
              <Button
                variant="primary"
                size="medium"
                disabled={salesforceRefreshLoading}
                onClick={handleRefreshFromSalesforce}
                style={{ width: 'auto' }}
              >
                <RefreshButtonContent>
                  {salesforceRefreshLoading && <CircularProgress size={18} color="inherit" />}
                  <span>
                    {salesforceRefreshLoading ? 'Refreshing...' : 'Refresh From Salesforce'}
                  </span>
                </RefreshButtonContent>
              </Button>
            </RefreshButtonWrap>
          </SalesforceRefreshRow>
          {shownLandlords.filter((item) => item.check === true).length > 0 && (
            <BuilEmailContainer>
              <p>{shownLandlords.filter((item) => item.check === true).length} row(s) selected</p>
              <Button
                variant="primary"
                size="large"
                disabled={inviteLoading && awaitingInvite}
                onClick={() => {
                  dispatch(successNotif(null));
                  dispatch(failedNotif(null));
                  setAwaitingInvite(false);
                  setIsOpenLayber(true);
                }}
              >
                Bulk Email Composer
              </Button>
            </BuilEmailContainer>
          )}
          <InviteLandlordTable
            landlords={shownLandlords}
            setOpen={setIsOpenLayber}
            filter={filter}
            updateLandlord={setShownLandlords}
            onRequestExportData={handleRequestExportData}
            onRequestFullExportData={handleRequestFullExportData}
            exportFullDatasetTotal={fullExportDatasetTotal}
            dropFilterActiveSummary={dropFilterActiveSummary}
            dropFilterActiveCount={dropFilterActiveCount}
            dropFilter={{
              component: (
                <DropFilterContainer>
                  <Dropdown
                    onChange={(e) => {
                      setSelectedBuildingsDraft(e.target.value);
                    }}
                    options={numOfBuildings}
                    value={selectedBuilingsDraft}
                    multiselect={true}
                    showSearch={false}
                    placeholder=""
                    placeholderFlag={false}
                    label="Property Count"
                    style={{
                      width: '100%',
                    }}
                  />
                  <Dropdown
                    onChange={(e) => {
                      setSelectedUnitsDraft(e.target.value);
                    }}
                    options={numOfUnits}
                    value={selectedUnitsDraft}
                    multiselect={true}
                    showSearch={false}
                    placeholder=""
                    placeholderFlag={false}
                    label="Unit Count"
                    style={{
                      width: '100%',
                    }}
                  />

                  <RangeDatePickerContainer>
                    <DatePickerInput
                      label={'Invited Date'}
                      size="small"
                      value={selectedInvitedDateDraft}
                      onChange={(date) => {
                        setSelectedInvitedDateDraft(date.target.value);
                      }}
                    />
                  </RangeDatePickerContainer>
                </DropFilterContainer>
              ),
              applyFunc: applyFunc,
              resetFunc: resetFunc,
            }}
            onChangeFilter={setFilter}
            pagination={{
              ...pagination,
              totalItems: landlordsJSON.total || landlordsJSON.data.length,
              totalPages: Math.ceil(
                (landlordsJSON.total || landlordsJSON.data.length) / pagination.rowsPerPage,
              ),
            }}
            onChangePagination={setPagination}
            sortOptions={sortOptions}
            onChangeSort={setSortOptions}
            onClickLandlord={onClickLandlord}
            inviteSending={inviteLoading && awaitingInvite}
          />
        </TableArea>
      ) : (
        <Loading size="large" label="Loading landlords..." />
      )}
    </DashboardLayoutContainer>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const RangeDatePickerContainer = styled.div`
  display: flex;
  max-width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
  * {
    box-sizing: border-box;
  }
  & > div {
    width: 100% !important;
  }

  .ui-kit-input-body {
    width: 278px;
  }
`;

const SalesforceRefreshRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
  width: 100%;
`;

const RefreshButtonWrap = styled.div`
  display: inline-flex;
  flex: 0 0 auto;

  button {
    width: auto !important;
    min-width: unset;
  }

  button:disabled {
    background-color: #702572 !important;
    border-color: #702572 !important;
    color: #fff !important;
    opacity: 0.85;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const RefreshButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const TableArea = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  z-index: 2;
`;

const BuilEmailContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 30px;
  p {
    margin: 0;
    color: rgba(144, 56, 139, 1);
    font-size: 14px;
    font-weight: bold;
    white-space: nowrap;
  }

  button {
    font-size: 14px;
    width: 170px;
  }
`;
